import {
  prepareAtomicEvmRecovery,
  evmRecoveryOutputDeliveries,
} from "./onchainRecoveryEvmPublish";
import { ethers } from "ethers";
import { readFeeNote, type PrivateFeeQuote } from "./privateFeeNotes";
import type { JoinSplitNote } from "../joinsplit/types";
import { computeNullifier } from "../joinsplit/keys";
import {
  encryptInboxNoteWithPublicKey,
  encryptInboxMessageWithPublicKey,
  formatInboxPublicKeyHex,
} from "../utils/inbox-crypto";
import { requestPrivateFees } from "../utils/relayer-client";
import { EVM_CONFIG } from "../config";
import {
  checkPrivateFeeApproval,
  privateFeeApprovalFor,
  refreshPrivateFeePolicy,
} from "./privateFeeApproval";
import {
  readFlatJoinSplitV2PoolSnapshot,
  resolveFlatJoinSplitV2Action,
  selectFlatJoinSplitV2Arity,
  type FlatJoinSplitV2ProveRequest,
} from "./sdk";
import {
  loadSpendTree,
  pinnedReadProvider,
  requireProvider,
} from "./poolAccess";
import {
  authenticateAndDedupeCandidates,
  candidateNotes,
} from "./noteSelection";
import { selectForAmount } from "./spendSelection";
import { createFlatJoinSplitV2Note, flatFieldHex } from "./notes";
import { inputWitness, outputWitness, validateInputs } from "./witness";
import { provePrivateFeePair } from "./pairedProofs";
import { sendSimulated, progressLog } from "./settlement";
import { prepareFlatJoinSplitV2SettlementJournal } from "./pendingJournal";
import { persistFlatJournalBackup } from "./journalBackup";
import { decimalScalar } from "./common";
import { getBaseReadProvider } from "../utils/base-read-provider";
import {
  privatePoolForToken,
  privateTokenPool,
  privateSpendSource,
  privatePools,
} from "./privatePools";
import { privateSwapDeployment, privateSwapEscrow } from "./privateSwapConfig";
import {
  privateBridgeEscrow,
  privateCcipBridgeEscrow,
  ccipEvmReceiver,
  CCIP_EXTRA_ARGS,
} from "./privateBridgeConfig";
import type { PrivateBridgeQuote } from "./privateBridge";
import type { FlatJoinSplitV2SwapOutputSeed } from "./swapOutput";
import type { PrivateFeePolicy } from "../base-app/hooks/usePrivateFeePolicy";
import type {
  FlatJoinSplitV2OperationResult,
  FlatJoinSplitV2TransferParams,
  FlatJoinSplitV2WithdrawParams,
  FlatJoinSplitV2MigrateParams,
} from "./operationTypes";

export const PRIVATE_FEE_INTERFACE = new ethers.Interface([
  "function settlePrivateFees((bytes32 merkleRoot,bytes32[] nullifiers,bytes32[] commitments,address token,address recipient,uint256 publicAmount,bytes proof) data,(bytes32 intentHash,bytes32 termsHash,bytes32 nonce,uint64 issuedAt,uint64 deadline,uint8 kind) quote,bytes feeProof,bytes quoteSignature)",
  "function privateFeeController() view returns(address)",
]);
export function usesPrivateFees(
  pool = EVM_CONFIG.CONTRACTS.SHIELDED_POOL_JOINSPLIT,
): boolean {
  return (
    !!process.env.REACT_APP_PRIVATE_FEE_POOL &&
    (pool.toLowerCase() ===
      process.env.REACT_APP_PRIVATE_FEE_POOL.toLowerCase() ||
      pool.toLowerCase() === privateTokenPool()?.toLowerCase() ||
      pool.toLowerCase() === privateSwapDeployment()?.pool.toLowerCase())
  );
}
export { readFeeNote, type PrivateFeeQuote } from "./privateFeeNotes";

/** Paired proofs use the unchanged conservation circuit and the new allocation
 * circuit. Only ciphertexts and completed proofs cross the wallet boundary. */
export async function preparePrivateFeeSpend(
  params:
    | FlatJoinSplitV2TransferParams
    | FlatJoinSplitV2WithdrawParams
    | (FlatJoinSplitV2MigrateParams & { amount: bigint }),
  kind: 0 | 1 | 2 | 3 | 4,
  swapOptions?: {
    pool: string;
    policy: PrivateFeePolicy;
    swap?: any;
    seed?: FlatJoinSplitV2SwapOutputSeed;
    bridge?: any;
    bridgeQuote?: PrivateBridgeQuote;
  },
) {
  const token = ethers.getAddress(params.tokenAddress),
    pool =
      swapOptions?.pool ||
      params.poolAddress ||
      privateSpendSource(params.notes, token, params.amount).pool;
  if (!privatePools().includes(ethers.getAddress(pool)))
    throw Error("Unknown private balance");
  if (!usesPrivateFees(pool))
    throw Error("This asset does not support private fee settlement.");
  // Capture the amount the user accepted before asynchronous preparation or
  // background fee refreshes can change the displayed policy.
  const approval = swapOptions?.policy || privateFeeApprovalFor(pool, token);
  const { provider, chainId } = await requireProvider(params.signer);
  const current = Number(
    await new ethers.Contract(
      pool,
      ["function currentTreeIndex() view returns(uint256)"],
      provider,
    ).currentTreeIndex(),
  );
  const maintenance = kind === 2 || kind === 4,
    isExit = kind === 1 || kind === 3;
  const migration = params as FlatJoinSplitV2MigrateParams;
  const sourceTree = kind === 2 ? migration.sourceTreeIndex : current;
  const allow =
    kind === 2 && migration.commitments
      ? new Set(migration.commitments.map((c) => c.toLowerCase()))
      : null;
  let available = (
    await authenticateAndDedupeCandidates({
      candidates: candidateNotes(params.notes, pool, chainId, token),
      pool,
      chainId,
      tokenAddress: token,
      spendingKeys: params.spendingKeys,
    })
  ).filter(
    (n) =>
      n.note.treeIndex === sourceTree &&
      (!allow || allow.has(n.note.commitment.toLowerCase())),
  );
  if (
    maintenance &&
    (!available.length ||
      available.length > 32 ||
      (allow && allow.size !== available.length))
  )
    throw Error("Refresh your balance before continuing.");
  const selected = maintenance
      ? available
      : selectForAmount(available, params.amount),
    total = selected.reduce((n, item) => n + item.note.value, 0n);
  params.onLog("Reading deposit history…", "info");
  const [tree, actualController] = await Promise.all([
    loadSpendTree(chainId, pool, sourceTree),
    new ethers.Contract(
      pool,
      PRIVATE_FEE_INTERFACE,
      provider,
    ).privateFeeController(),
  ]);
  const snapshot = await readFlatJoinSplitV2PoolSnapshot(
    pinnedReadProvider(provider),
    pool,
    token,
    tree.root,
  );
  params.onLog("Validating private inputs…", "info");
  await validateInputs({
    provider,
    pool,
    selected,
    tokenAddress: token,
    spendingKeys: params.spendingKeys,
    expectedTokenHash: snapshot.sourceTokenHash,
  });
  const nullifiers = await Promise.all(
    selected.map(({ note }) =>
      computeNullifier(
        params.spendingKeys.nullifyingKey,
        note.tokenHash,
        note.leafIndex,
      ),
    ),
  );
  let recipient =
    isExit && !swapOptions
      ? ethers.getAddress((params as FlatJoinSplitV2WithdrawParams).recipient)
      : ethers.ZeroAddress;
  params.onLog("Checking accepted fees…", "info");
  const quote = await requestPrivateFees<PrivateFeeQuote>(
    "prepare",
    {
      kind,
      grossAmount: params.amount.toString(),
      merkleRoot: tree.root,
      nullifiers,
      sourceTreeIndex: sourceTree,
      outputTreeIndex: current,
      recipient,
      token,
      maxGasReimbursement: approval.gasReimbursementAmount,
      ...(swapOptions?.swap ? { swap: swapOptions.swap } : {}),
      ...(swapOptions?.bridge ? { bridge: swapOptions.bridge } : {}),
      ...(kind === 3
        ? { redemptionMpk: flatFieldHex(params.spendingKeys.mpk) }
        : {}),
    },
    {
      pool,
      signal: params.signal,
      requestKey: ethers.hexlify(ethers.randomBytes(32)),
    },
  ).catch((error) => {
    if (error?.code === "FEE_ESTIMATE_CHANGED")
      refreshPrivateFeePolicy(approval);
    throw error;
  });
  if (
    quote.chainId !== Number(chainId) ||
    ethers.getAddress(quote.pool) !== ethers.getAddress(pool) ||
    ethers.getAddress(quote.request?.token || ethers.ZeroAddress) !== token ||
    quote.request?.gasFor !== undefined ||
    quote.terms.length !== 10 ||
    quote.feeOutputs.length !== 3 ||
    BigInt(quote.amounts.grossAmount) !== params.amount ||
    (!maintenance &&
      (BigInt(quote.amounts.recipientAmount) <= 0n ||
        BigInt(quote.amounts.recipientAmount) +
          BigInt(quote.amounts.totalFee) !==
          params.amount))
  )
    throw Error("Fee quote does not match this transaction.");
  checkPrivateFeeApproval(
    quote,
    swapOptions?.bridge
      ? "bridge"
      : swapOptions?.swap
        ? "swap"
        : kind === 0
          ? "p2p"
          : "withdraw",
    kind >= 2,
    approval,
  );
  if (swapOptions?.swap) {
    const route = quote.request?.swapRoute,
      expected = {
        sourcePool: pool,
        inputToken: token,
        amountIn: quote.amounts.recipientAmount,
        ...swapOptions.swap,
      };
    if (
      !route ||
      Object.keys(expected).some(
        (k) =>
          String(route[k]).toLowerCase() !==
          String((expected as any)[k]).toLowerCase(),
      ) ||
      quote.request?.recipient !== privateSwapEscrow(route)
    )
      throw Error("Swap quote does not match the requested route");
    recipient = quote.request.recipient;
  }
  if (swapOptions?.bridge) {
    const route = quote.request?.bridgeRoute,
      b = swapOptions.bridge,
      q = swapOptions.bridgeQuote!;
    if (b.provider === "ccip") {
      // The CCIP prepayment is bounded by the delivery fee the user saw plus the relayer's fixed margin.
      const expected = {
        sourcePool: pool,
        token,
        amount: quote.amounts.recipientAmount,
        destinationChainSelector: q.route.destinationChainSelector,
        receiver: ccipEvmReceiver(b.destinationAddress),
        extraArgs: CCIP_EXTRA_ARGS,
        refundRecipient: b.refundRecipient,
        deadline: b.deadline,
        salt: b.salt,
      };
      if (
        !route ||
        Object.keys(expected).some(
          (k) =>
            String(route[k]).toLowerCase() !==
            String((expected as any)[k]).toLowerCase(),
        ) ||
        BigInt(route.maxFee) <= 0n ||
        BigInt(route.maxFee) > (BigInt(q.deliveryWei) * 125n) / 100n ||
        quote.request?.recipient !== privateCcipBridgeEscrow(route)
      )
        throw Error("Bridge quote does not match the requested route");
    } else {
      const expected = {
        sourcePool: pool,
        token,
        amount: quote.amounts.recipientAmount,
        tokenId: q.route.tokenId,
        destinationChain: q.route.destinationChain,
        destinationAddress: b.destinationAddress,
        refundRecipient: b.refundRecipient,
        deadline: b.deadline,
        salt: b.salt,
      };
      if (
        !route ||
        Object.keys(expected).some(
          (k) =>
            String(route[k]).toLowerCase() !==
            String((expected as any)[k]).toLowerCase(),
        ) ||
        BigInt(route.gasValue) <= 0n ||
        BigInt(route.gasValue) > BigInt(approval.gasReimbursementWei) ||
        quote.request?.recipient !== privateBridgeEscrow(route)
      )
        throw Error("Bridge quote does not match the requested route");
    }
    recipient = quote.request!.recipient!;
  }
  if (actualController !== ethers.getAddress(quote.controller))
    throw Error("Fee service configuration changed.");
  const outputHash =
    kind === 2 ? snapshot.currentTokenHash : snapshot.sourceTokenHash;
  const makeNote = (mpk: bigint, value: bigint) =>
    createFlatJoinSplitV2Note({
      recipientMpk: mpk,
      value,
      chainId,
      poolAddress: pool,
      treeIndex: current,
      tokenAddress: token,
      tokenHash: outputHash,
    });
  const transfer = params as FlatJoinSplitV2TransferParams;
  const recipientNote =
    kind === 0
      ? {
          ...(await makeNote(
            transfer.recipientMpk,
            BigInt(quote.amounts.recipientAmount),
          )),
          privateHistory: transfer.recipientPrivateHistory
            ? {
                ...transfer.recipientPrivateHistory,
                amount: ethers.formatUnits(
                  quote.amounts.recipientAmount,
                  params.tokenDecimals ?? 18,
                ),
              }
            : undefined,
        }
      : null;
  const changeAmount = maintenance
    ? total - BigInt(quote.amounts.totalFee)
    : total - params.amount;
  if (maintenance && changeAmount <= 0n)
    throw Error("This token balance does not cover the network fee.");
  const change =
    changeAmount > 0n
      ? await makeNote(params.spendingKeys.mpk, changeAmount)
      : null;
  const fees = quote.feeOutputs.map((n) => (n ? readFeeNote(n) : null));
  const logical = [recipientNote, change, ...fees],
    outputs = logical.filter((n): n is JoinSplitNote => n !== null);
  const ownedOrSent = [recipientNote, change].filter(
    (n): n is JoinSplitNote => n !== null,
  );
  const deliveries = ownedOrSent.map((note) => ({
    commitment: note.commitment,
    recipientMpkHex: flatFieldHex(
      note === recipientNote ? transfer.recipientMpk : params.spendingKeys.mpk,
    ),
    recipientInboxPublicKeyHex:
      note === recipientNote
        ? transfer.recipientInboxPublicKeyHex
        : formatInboxPublicKeyHex(params.spendingKeys.spendingKey),
    keepLocal:
      note === change ||
      (kind === 0 && transfer.recipientMpk === params.spendingKeys.mpk),
  }));
  const recovery = await prepareAtomicEvmRecovery({
    provider,
    chainId: Number(chainId),
    pool,
    outputs: evmRecoveryOutputDeliveries(
      ownedOrSent,
      params.spendingKeys,
      deliveries,
    ),
    feeOutputs: quote.feeOutputs,
    seeds: swapOptions?.seed
      ? [
          swapOptions.seed,
          { ...swapOptions.seed, outputTokenAddress: token },
        ].map((seed) => ({
          seed,
          pool,
          inboxPublicKey: formatInboxPublicKeyHex(
            params.spendingKeys.spendingKey,
          ),
        }))
      : [],
    signal: params.signal,
  });
  const recoveryDigest = recovery?.digest;
  const action =
    kind === 2
      ? resolveFlatJoinSplitV2Action({
          kind: "migrate",
          snapshot,
          recoveryDigest,
        })
      : !isExit
        ? resolveFlatJoinSplitV2Action({
            kind: "joinsplit",
            snapshot,
            recoveryDigest,
          })
        : resolveFlatJoinSplitV2Action({
            kind: "withdraw",
            snapshot,
            recoveryDigest,
            recipient,
            fee: 0n,
            outputCount: outputs.length ? 1 : 0,
          });
  const inputs = selected.map(({ note }) =>
    inputWitness(note, tree.path(note.commitment), params.spendingKeys),
  );
  const request: FlatJoinSplitV2ProveRequest = {
    arity_id: selectFlatJoinSplitV2Arity(inputs.length, outputs.length),
    merkle_root: tree.root,
    inputs,
    outputs: outputs.map(outputWitness),
    withdraw_amount: decimalScalar(
      isExit ? BigInt(quote.amounts.recipientAmount) : 0n,
    ),
    action_token_hash: action.actionTokenHash,
    output_token_hash: action.outputTokenHash,
    action_context: action.actionContext,
    spend_secret_scalar: decimalScalar(params.spendingKeys.spendSecretScalar),
  };
  const first = inputs[0];
  const { bundle, feeProof } = await provePrivateFeePair(
    request,
    (accumulator) => ({
      public: [
        tree.root,
        nullifiers[0],
        snapshot.sourceTokenHash,
        outputHash,
        accumulator,
        String(outputs.length),
        request.withdraw_amount,
        String(kind),
        quote.termsHash,
      ],
      firstInput: {
        npk: first.npk,
        tokenHash: first.token_hash,
        value: first.value,
        random: first.random,
        leafIndex: first.leaf_index,
        nullifyingKey: first.nullifying_key,
        pubKeyX: first.pub_key_x,
        pubKeyY: first.pub_key_y,
        pathElements: first.path_elements,
        pathIndices: first.path_indices,
      },
      outputs: logical.map((n) =>
        n
          ? {
              npk: n.npk.toString(),
              value: n.value.toString(),
              random: n.random.toString(),
            }
          : { npk: "0", value: "0", random: "0" },
      ),
      grossAmount: params.amount.toString(),
      terms: quote.terms,
    }),
    { signal: params.signal, onProgress: progressLog(params.onLog) as any },
  );
  const outputRecovery = await Promise.all(
    ownedOrSent.map(async (note, i) => {
      if (!deliveries[i].recipientInboxPublicKeyHex)
        throw Error(
          "Ask the recipient for their current private address or .stealth handle.",
        );
      return {
        role: "payment",
        commitment: note.commitment,
        recipientKey: ethers.sha256(
          ethers.toUtf8Bytes(deliveries[i].recipientMpkHex.toLowerCase()),
        ),
        envelope: await encryptInboxNoteWithPublicKey(
          note,
          deliveries[i].recipientInboxPublicKeyHex!,
        ),
      };
    }),
  );
  const settlement = {
    merkleRoot: tree.root,
    nullifiers,
    commitments: outputs.map((n) => n.commitment),
    token,
    recipient,
    publicAmount: request.withdraw_amount,
    proof: bundle.proof.router_payload,
  };
  const accountAddress = await params.signer.getAddress(),
    journalScope = {
      chainId,
      poolAddress: pool,
      accountAddress,
      spendingKey: params.spendingKeys.spendingKey,
    };
  // Backed up by the server before releasing a usable withdrawal signature.
  const swapRecovery = swapOptions?.swap
    ? await encryptInboxMessageWithPublicKey(
        JSON.parse(
          JSON.stringify(
            {
              version: 1,
              seed: swapOptions.seed,
              accountAddress,
              sourceCommitments: selected.map((n) => n.note.commitment),
              sourceNullifiers: nullifiers,
              outputNotes: ownedOrSent,
              deliveries,
            },
            (_, v) => (typeof v === "bigint" ? v.toString() : v),
          ),
        ),
        formatInboxPublicKeyHex(params.spendingKeys.spendingKey),
      )
    : undefined;
  const bridgeRecovery = swapOptions?.bridge
    ? await encryptInboxMessageWithPublicKey(
        JSON.parse(
          JSON.stringify(
            {
              version: 1,
              ownerMpk: params.spendingKeys.mpk,
              accountAddress,
              sourceCommitments: selected.map((n) => n.note.commitment),
              sourceNullifiers: nullifiers,
              outputNotes: ownedOrSent,
              deliveries,
            },
            (_, v) => (typeof v === "bigint" ? v.toString() : v),
          ),
        ),
        formatInboxPublicKeyHex(params.spendingKeys.spendingKey),
      )
    : undefined;
  const authorizationInput = {
    id: quote.id,
    ...(recovery ? { recoveryRecords: recovery.records } : {}),
    settlement,
    feeProof,
    outputRecovery,
    ...(swapRecovery ? { swapRecovery } : {}),
    ...(bridgeRecovery ? { bridgeRecovery } : {}),
  };
  params.onLog("Authorizing private settlement…", "info");
  const authorized = await requestPrivateFees<PrivateFeeQuote>(
    swapOptions?.bridge
      ? "bridge-authorize"
      : swapOptions?.swap
        ? "swap-authorize"
        : "authorize",
    authorizationInput,
    { pool, signal: params.signal },
  );
  if (!authorized.authorization)
    throw Error("Transaction authorization was not received.");
  return {
    pool,
    provider,
    chainId,
    quote,
    authorizationInput,
    recovery,
    authorized,
    selected,
    ownedOrSent,
    change,
    leg: {
      settlement,
      quote: authorized.authorization.quote,
      feeProof,
      signature: authorized.authorization.signature,
      ...(recovery ? { recoveryRecords: recovery.records } : {}),
    },
    accountAddress,
    journalScope,
    async journal(signed: {
      to: string;
      data: `0x${string}`;
      value: bigint;
      chainId: bigint;
    }) {
      params.onLog("Saving encrypted recovery data…", "info");
      const journal = await prepareFlatJoinSplitV2SettlementJournal(
        journalScope,
        {
          kind: swapOptions?.swap
            ? "swap"
            : kind === 2
              ? "migrate"
              : isExit
                ? "withdraw"
                : "joinsplit",
          ...(swapOptions?.swap ? { swapOutputSeed: swapOptions.seed } : {}),
          transaction: {
            ...signed,
            from: accountAddress,
            value: signed.value.toString(),
            chainId: Number(chainId),
          },
          sourceCommitments: selected.map((n) => n.note.commitment),
          sourceNullifiers: nullifiers,
          outputNotes: ownedOrSent,
          deliveries,
        },
      );
      await persistFlatJournalBackup(journalScope, journal.id);
      return { id: journal.id, scope: journalScope };
    },
  };
}

export async function executePrivateFeeSpend(
  params:
    | FlatJoinSplitV2TransferParams
    | FlatJoinSplitV2WithdrawParams
    | (FlatJoinSplitV2MigrateParams & { amount: bigint }),
  kind: 0 | 1 | 2 | 3 | 4,
): Promise<FlatJoinSplitV2OperationResult> {
  try {
    const prepared = await preparePrivateFeeSpend(params, kind);
    const transaction = {
      to: prepared.pool,
      data: PRIVATE_FEE_INTERFACE.encodeFunctionData("settlePrivateFees", [
        prepared.leg.settlement,
        prepared.leg.quote,
        prepared.leg.feeProof,
        prepared.leg.signature,
      ]) as `0x${string}`,
      value: 0n,
      chainId: prepared.chainId,
    };
    if (prepared.recovery)
      transaction.data = prepared.recovery.wrap(transaction.data);
    const settled = await sendSimulated(
      params.signer,
      prepared.provider,
      transaction,
      prepared.pool,
      prepared.ownedOrSent,
      params.onLog,
      prepared.accountAddress,
      (signed) => prepared.journal(signed),
      true,
      params.signal,
      undefined,
      getBaseReadProvider(),
      () =>
        requestPrivateFees("submit", prepared.authorizationInput, {
          pool: prepared.pool,
          signal: params.signal,
        }),
    );
    return {
      success: true,
      txHash: settled.txHash,
      spentNotes: prepared.selected.map((n) => n.note),
      outputNotes: settled.outputs,
      changeNote: prepared.change
        ? settled.outputs.find(
            (n) => n.commitment === prepared.change!.commitment,
          )
        : undefined,
      journalId: settled.journalId,
    };
  } catch (error) {
    return {
      success: false,
      spentNotes: [],
      outputNotes: [],
      error: error instanceof Error ? error.message : "Transaction failed",
    };
  }
}
